/**
 * modal-layer — shared a11y mechanics for modal overlays (E10-F02-T06).
 *
 * Both the centred {@link Dialog} (E10-F02-T01) and the slide-over `Drawer`
 * (the Company Research evidence-drawer, E07 Review) are "modal layers": while
 * open they own keyboard focus, dim the page, and must restore focus to the
 * element that opened them. The legacy production front-end had NO focus-trap
 * and NO return-focus on its native `<dialog>`s and its evidence-drawer (Esc
 * worked for the drawer, but focus leaked to the page behind it). This module
 * is the single source of truth that closes that gap for every overlay so the
 * a11y contract can never drift between primitives:
 *
 *   - on open, move focus INTO the panel (first focusable, else the panel);
 *   - trap Tab / Shift+Tab so focus wraps at the boundary and never escapes;
 *   - Esc requests close (unless suppressed);
 *   - on close (unmount), restore focus to the previously-focused invoker;
 *   - mark the rest of the page inert (`aria-hidden`) so AT and the tab order
 *     ignore the background — the "background is blocked" requirement.
 *
 * It is presentation-agnostic: it only touches focus, keyboard and the
 * `aria-hidden` of sibling roots. The visual chrome (scrim, panel, slide
 * direction) lives in the primitives that consume the hook.
 *
 * IMPORTANT — the overlay MUST be portalled to `document.body` (see
 * {@link ModalPortal}). The whole SPA mounts under a single `#root`; if the
 * overlay rendered INLINE it would live INSIDE `#root` alongside the page it is
 * meant to block, so the background-block (which inerts the overlay root's
 * SIBLINGS) would have no app-content sibling to inert and the page behind would
 * stay focusable + visible to AT — the exact legacy gap this module closes. By
 * portalling the overlay to `<body>`, the overlay root becomes a sibling of
 * `#root`, and inerting siblings now correctly inerts the real page.
 */
import {
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/** Selector for elements that can receive focus inside an overlay panel. */
export const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Returns the focusable descendants of `root` in DOM order. We deliberately do
 * NOT filter on `offsetParent`/`getClientRects()` for visibility: those are
 * layout-engine properties that are always falsy under jsdom (no layout), which
 * would silently disable the focus-trap in tests AND mis-handle
 * off-screen-but-focusable nodes. The selector already excludes `[disabled]`,
 * `type="hidden"` and `tabindex="-1"`; an explicit `hidden`/`aria-hidden`
 * ancestor is the only additional case we guard against (it removes the node
 * from the tab order in real browsers too).
 */
export function focusableWithin(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.closest('[hidden],[aria-hidden="true"]'),
  );
}

export interface UseModalLayerOptions {
  /** Whether the overlay is currently open (mounted). */
  readonly open: boolean;
  /** Close request — fired by Esc (unless {@link disableEscapeClose}). */
  readonly onClose: () => void;
  /** Disable Esc-to-close. Default false (Esc closes). */
  readonly disableEscapeClose?: boolean;
  /**
   * When true, every top-level sibling of the panel's overlay root is marked
   * `aria-hidden` / `inert` while the layer is open, so the page behind it is
   * removed from the accessibility tree and the tab order (background blocked).
   * Default true. Set false only for non-modal overlays.
   */
  readonly blockBackground?: boolean;
}

export interface UseModalLayerResult<T extends HTMLElement> {
  /** Attach to the panel element — the focus-trap boundary + keydown target. */
  readonly panelRef: React.RefObject<T | null>;
  /** Wire to the panel's `onKeyDown`: handles Esc + the Tab focus-trap. */
  readonly handleKeyDown: (event: ReactKeyboardEvent<T>) => void;
}

/**
 * Walks up from `node` to the element whose parent is `<body>` — i.e. the
 * top-level root that contains the overlay. We hide its SIBLINGS, not the root
 * itself (the overlay lives inside the root), so the panel keeps its a11y while
 * the rest of the page goes inert.
 */
function topLevelRootOf(node: HTMLElement): HTMLElement | null {
  let current: HTMLElement | null = node;
  const body = node.ownerDocument.body;
  while (current && current.parentElement && current.parentElement !== body) {
    current = current.parentElement;
  }
  return current && current.parentElement === body ? current : null;
}

/**
 * The reusable modal-layer mechanics. Returns a `panelRef` to put on the panel
 * and a `handleKeyDown` to wire to its `onKeyDown`. Mount the panel only while
 * `open` (the consumer renders `null` when closed) — the hook keys all of its
 * effects on `open` and restores focus in the cleanup that runs when `open`
 * flips to false (or the component unmounts).
 */
export function useModalLayer<T extends HTMLElement = HTMLElement>({
  open,
  onClose,
  disableEscapeClose = false,
  blockBackground = true,
}: UseModalLayerOptions): UseModalLayerResult<T> {
  const panelRef = useRef<T | null>(null);
  // The element focused right before the layer opened, restored on close.
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // ONE ordered effect owns the open/close lifecycle so the steps can never run
  // out of order across separate effects (which matters now the overlay is
  // portalled to <body>: the invoker can itself be a body-level sibling that the
  // background-block inerts, and an inert element cannot take focus):
  //
  //   open:  capture invoker → block background → move focus INTO the panel
  //   close: un-block background → restore focus to the invoker
  //
  // Capture happens BEFORE blocking (so inerting the invoker can't blur it out
  // from under us), and on close we un-block BEFORE restoring (so the invoker is
  // focusable again when we put focus back).
  useEffect(() => {
    if (!open) return;

    // 1. Capture the invoker first — before anything can move/blur focus.
    previouslyFocused.current = (document.activeElement as HTMLElement | null) ?? null;

    // 2. Block the background: mark the panel-root's siblings aria-hidden + inert
    //    so the page behind the layer is out of the a11y tree AND the tab order.
    //    We record each sibling's prior values to restore them EXACTLY (never
    //    clobber a sibling that was already legitimately hidden).
    const panel = panelRef.current;
    let restore: { el: HTMLElement; ariaHidden: string | null; inert: boolean }[] = [];
    if (blockBackground) {
      const root = panel ? topLevelRootOf(panel) : null;
      if (root && root.parentElement) {
        const siblings = Array.from(root.parentElement.children).filter(
          (el): el is HTMLElement => el instanceof HTMLElement && el !== root,
        );
        restore = siblings.map((el) => ({
          el,
          ariaHidden: el.getAttribute('aria-hidden'),
          inert: el.hasAttribute('inert'),
        }));
        for (const el of siblings) {
          el.setAttribute('aria-hidden', 'true');
          el.setAttribute('inert', '');
        }
      }
    }

    // 3. Move focus INTO the panel (first focusable, else the panel itself).
    const focusables = focusableWithin(panel);
    (focusables[0] ?? panel)?.focus();

    return () => {
      // a. Un-block the background first, so the invoker is focusable again.
      for (const { el, ariaHidden, inert } of restore) {
        if (ariaHidden === null) el.removeAttribute('aria-hidden');
        else el.setAttribute('aria-hidden', ariaHidden);
        if (!inert) el.removeAttribute('inert');
      }
      // b. Then restore focus to the invoker — parity with native <dialog>.
      previouslyFocused.current?.focus?.();
    };
  }, [open, blockBackground]);

  // Esc-to-close + Tab focus-trap. Handled on the panel's keydown so it only
  // fires while focus is inside the layer (the trap keeps it there anyway).
  const handleKeyDown = useCallback(
    (event: ReactKeyboardEvent<T>): void => {
      if (event.key === 'Escape') {
        if (disableEscapeClose) return;
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      // Focus-trap: wrap Tab / Shift+Tab around the focusable boundary so focus
      // never leaves the layer while it is open.
      const focusables = focusableWithin(panelRef.current);
      if (focusables.length === 0) {
        // Nothing focusable inside — keep focus on the panel itself.
        event.preventDefault();
        panelRef.current?.focus();
        return;
      }

      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [disableEscapeClose, onClose],
  );

  return { panelRef, handleKeyDown };
}

/**
 * Scrim (light-dismiss) handler factory. Returns an `onMouseDown` for the
 * backdrop element that closes only when a press both STARTS and LANDS on the
 * scrim itself — a drag that started inside the panel and released on the scrim
 * must not dismiss. Shared by Dialog and Drawer so light-dismiss behaves
 * identically everywhere.
 */
export function useScrimDismiss<T extends HTMLElement>(
  onClose: () => void,
  disabled = false,
): (event: ReactMouseEvent<T>) => void {
  return useCallback(
    (event: ReactMouseEvent<T>): void => {
      if (disabled) return;
      if (event.target === event.currentTarget) onClose();
    },
    [onClose, disabled],
  );
}

/**
 * Portals the overlay (scrim + panel) to a freshly-created host appended to
 * `document.body`, so the overlay root sits as a SIBLING of the app root
 * (`#root`) rather than nested inside it. This is what makes the
 * background-block actually block: {@link useModalLayer} inerts the overlay
 * root's siblings, and once the overlay is a body-level sibling of `#root`,
 * those siblings include the real page.
 *
 * The host element is created lazily (lazy ref init, not in render side-effects)
 * so the first render already has a stable node, and removed on unmount. We use
 * a dedicated host per overlay instance rather than a single shared container so
 * concurrent overlays (e.g. a dialog opened from within a drawer) each get their
 * own body-level root and block each other correctly.
 *
 * `useEffect` (not `useLayoutEffect`) for the append/remove is sufficient — the
 * portal children only commit into the host after it is mounted, and React
 * flushes the effect before paint for the user. Guarding `children` to `null`
 * while closed keeps the host empty (and the consumer already renders `null`
 * when `open` is false, so the whole subtree — host included — unmounts then).
 */
export function ModalPortal({ children }: { readonly children: ReactNode }): React.ReactPortal {
  // Lazy, stable host for this overlay instance.
  const hostRef = useRef<HTMLDivElement | null>(null);
  if (hostRef.current === null) {
    hostRef.current = document.createElement('div');
    hostRef.current.setAttribute('data-modal-portal', '');
  }

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    document.body.appendChild(host);
    return () => {
      host.remove();
    };
  }, []);

  return createPortal(children, hostRef.current);
}
